import React, { useEffect, useRef, lazy } from 'react';
const OrganizationsFounded = ({
  language = 'en'
}) => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const translations = {
    en: {
      organizations: 'Organizations',
      founded: 'Founded & Led',
      periclestitle: 'Pericles Capital',
      periclesSubtitle: 'Quantitative Investment Fund',
      periclesRole: 'Co-Founder & Chief Investment Officer',
      airevoTitle: 'Airevo',
      airevoSubtitle: 'Prop-Tech Real Estate AI Startup (Ended)',
      airevoRole: 'Co-Founder & CEO',
      visitWebsite: 'Visit Website',
      aidworksTitle: 'AidWorks Foundation',
      aidworksSubtitle: 'Nonprofit Organization',
      aidworksRole: 'Founder & CEO',
      aidworksVolunteers: '100+ Volunteers',
      aidworksImpressions: '100K+ Impressions',
      nibgTitle: 'NIBG',
      nibgSubtitle: 'Northeastern Investment Banking Group',
      nibgRole: 'Interim President',
      nibgMembers: '30+ Members'
    },
    vi: {
      organizations: 'Tổ Chức',
      founded: 'Sáng Lập & Lãnh Đạo',
      periclestitle: 'Pericles Capital',
      periclesSubtitle: 'Quỹ Đầu Tư Định Lượng',
      periclesRole: 'Đồng Sáng Lập & Giám Đốc Đầu Tư',
      airevoTitle: 'Airevo',
      airevoSubtitle: 'Startup AI Bất Động Sản Công Nghệ (Đã Kết Thúc)',
      airevoRole: 'Đồng Sáng Lập & CEO',
      visitWebsite: 'Truy Cập Website',
      aidworksTitle: 'AidWorks Foundation',
      aidworksSubtitle: 'Tổ Chức Phi Lợi Nhuận',
      aidworksRole: 'Người Sáng Lập & CEO',
      aidworksVolunteers: '100+ Tình Nguyện Viên',
      aidworksImpressions: '100K+ Lượt Tiếp Cận',
      nibgTitle: 'NIBG',
      nibgSubtitle: 'Nhóm Ngân Hàng Đầu Tư Northeastern',
      nibgRole: 'Chủ Tịch Lâm Thời',
      nibgMembers: '30+ Thành Viên'
    }
  };
  const t = translations[language];
  return <section id="organizations" ref={sectionRef} className="w-full py-24 bg-background" data-id="element-472">
      <div className="container mx-auto px-4" data-id="element-473">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16 fade-in animate-pulse" data-id="element-474">
          {t.organizations} <span className="gradient-text" data-id="element-475">{t.founded}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-476">
          {/* Pericles Capital Website */}
          <div className="slide-in-left" data-id="element-477">
            <div className="bg-secondary rounded-2xl p-6 card-glow transform transition-all duration-500 hover:scale-105 hover:rotate-1" data-id="element-478">
              <h3 className="text-2xl font-display font-bold mb-4 flex flex-wrap items-center" data-id="element-479">
                <span className="gradient-text animate-pulse-slow mr-2" style={{
                background: 'linear-gradient(90deg, #8B5CF6, #F59E0B)'
              }} data-id="element-480">
                  {t.periclestitle}
                </span>
                <span className="text-muted text-sm" data-id="element-481">{t.periclesSubtitle}</span>
              </h3>
              <div className="website-embed-container mb-4 transform transition-all duration-500 hover:translate-y-[-10px]" data-id="element-482">
                <iframe src="https://periclescapital.org/" title="Pericles Capital Website" className="website-iframe" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" data-id="element-483"></iframe>
              </div>
              <div className="flex flex-wrap justify-between items-center" data-id="element-484">
                <div className="flex items-center mb-2 sm:mb-0" data-id="element-485">
                  <p className="text-muted text-sm mr-2" data-id="element-486">{t.periclesRole}</p>
                </div>
                <a href="https://periclescapital.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent-cyan hover:text-accent-violet transition-colors transform transition-all duration-300 hover:translate-x-2" data-id="element-487">
                  <span data-id="element-488">{t.visitWebsite}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link animate-bounce-slow" data-id="element-489">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" data-id="element-490"></path>
                    <polyline points="15 3 21 3 21 9" data-id="element-491"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3" data-id="element-492"></line>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          {/* Airevo Website */}
          <div className="slide-in-left" data-id="element-493">
            <div className="bg-secondary rounded-2xl p-6 card-glow transform transition-all duration-500 hover:scale-105 hover:rotate-1" data-id="element-494">
              <h3 className="text-2xl font-display font-bold mb-4 flex flex-wrap items-center" data-id="element-495">
                <span className="gradient-text animate-pulse-slow mr-2" style={{
                background: 'linear-gradient(90deg, #22D3EE, #4F46E5)'
              }} data-id="element-496">
                  {t.airevoTitle}
                </span>
                <span className="text-muted text-sm" data-id="element-497">{t.airevoSubtitle}</span>
              </h3>
              <div className="website-embed-container mb-4 transform transition-all duration-500 hover:translate-y-[-10px]" data-id="element-498">
                <iframe src="https://airevoapp.com" title="Airevo Website" className="website-iframe" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" data-id="element-499"></iframe>
              </div>
              <div className="flex flex-wrap justify-between items-center" data-id="element-500">
                <div className="flex items-center mb-2 sm:mb-0" data-id="element-501">
                  <p className="text-muted text-sm mr-2" data-id="element-502">{t.airevoRole}</p>
                </div>
                <a href="https://airevoapp.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent-cyan hover:text-accent-violet transition-colors transform transition-all duration-300 hover:translate-x-2" data-id="element-503">
                  <span data-id="element-504">{t.visitWebsite}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link animate-bounce-slow" data-id="element-505">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" data-id="element-506"></path>
                    <polyline points="15 3 21 3 21 9" data-id="element-507"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3" data-id="element-508"></line>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          {/* AidWorks Foundation Website */}
          <div className="slide-in-right" data-id="element-509">
            <div className="bg-secondary rounded-2xl p-6 card-glow transform transition-all duration-500 hover:scale-105 hover:rotate-[-1deg]" data-id="element-510">
              <h3 className="text-2xl font-display font-bold mb-4 flex flex-wrap items-center" data-id="element-511">
                <span className="gradient-text animate-pulse-slow mr-2" style={{
                background: 'linear-gradient(90deg, #22D3EE, #0891B2)'
              }} data-id="element-512">
                  {t.aidworksTitle}
                </span>
                <span className="text-muted text-sm" data-id="element-513">{t.aidworksSubtitle}</span>
              </h3>
              <div className="website-embed-container mb-4 transform transition-all duration-500 hover:translate-y-[-10px]" data-id="element-514">
                <iframe src="https://aidworksfoundation.org" title="AidWorks Foundation Website" className="website-iframe" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" data-id="element-515"></iframe>
              </div>
              <div className="flex flex-wrap justify-between items-center" data-id="element-516">
                <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-0" data-id="element-517">
                  <p className="text-muted text-sm" data-id="element-518">{t.aidworksRole}</p>
                  <span className="px-2 py-1 bg-accent-cyan/20 text-accent-cyan text-xs rounded-full" data-id="element-519">
                    {t.aidworksVolunteers}
                  </span>
                  <span className="px-2 py-1 bg-accent-violet/20 text-accent-violet text-xs rounded-full" data-id="element-520">
                    {t.aidworksImpressions}
                  </span>
                </div>
                <a href="https://aidworksfoundation.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent-cyan hover:text-accent-violet transition-colors transform transition-all duration-300 hover:translate-x-2" data-id="element-521">
                  <span data-id="element-522">{t.visitWebsite}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link animate-bounce-slow" data-id="element-523">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" data-id="element-524"></path>
                    <polyline points="15 3 21 3 21 9" data-id="element-525"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3" data-id="element-526"></line>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          {/* NIBG Website */}
          <div className="slide-in-left" data-id="element-527">
            <div className="bg-secondary rounded-2xl p-6 card-glow transform transition-all duration-500 hover:scale-105 hover:rotate-1" data-id="element-528">
              <h3 className="text-2xl font-display font-bold mb-4 flex flex-wrap items-center" data-id="element-529">
                <span className="gradient-text animate-pulse-slow mr-2" style={{
                background: 'linear-gradient(90deg, #8B5CF6, #F59E0B)'
              }} data-id="element-530">
                  {t.nibgTitle}
                </span>
                <span className="text-muted text-sm" data-id="element-531">{t.nibgSubtitle}</span>
              </h3>
              <div className="website-embed-container mb-4 transform transition-all duration-500 hover:translate-y-[-10px]" data-id="element-532">
                <iframe src="https://www.nuibg.com" title="NIBG Website" className="website-iframe" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" data-id="element-533"></iframe>
              </div>
              <div className="flex flex-wrap justify-between items-center" data-id="element-534">
                <div className="flex items-center mb-2 sm:mb-0" data-id="element-535">
                  <p className="text-muted text-sm mr-2" data-id="element-536">{t.nibgRole}</p>
                  <span className="px-2 py-1 bg-accent-violet/20 text-accent-violet text-xs rounded-full" data-id="element-537">
                    {t.nibgMembers}
                  </span>
                </div>
                <a href="https://www.nuibg.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent-cyan hover:text-accent-violet transition-colors transform transition-all duration-300 hover:translate-x-2" data-id="element-538">
                  <span data-id="element-539">{t.visitWebsite}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link animate-bounce-slow" data-id="element-540">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" data-id="element-541"></path>
                    <polyline points="15 3 21 3 21 9" data-id="element-542"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3" data-id="element-543"></line>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default OrganizationsFounded;